import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import {
  AccountCircle,
  Security,
  History,
  Help,
  Speed,
  NotificationsActive,
  Save,
  Edit,
  Visibility,
  VisibilityOff,
  Info,
  AccessTime,
  Business,
  Email,
  Phone,
  Web,
  Description,
  Key,
  ContentCopy,
  Refresh,
  Add,
  Psychology,
  SmartToy,
  Person,
  Cancel,
  LocationOn,
} from "@mui/icons-material";
import CoPilotSettingsTab from "../components/CoPilot/CoPilotSettingsTab";
import { AIConfigPanel } from "../components/AI";
// Reserved for RBAC-wrapped settings sections (UAM Phase 5.2.3)
// eslint-disable-next-line @typescript-eslint/no-unused-vars -- import kept for upcoming PermissionGuard usage
import { PermissionGuard } from "../components/Permissions/PermissionGuard";
import { formatApiTokenError } from "../utils/errorParser";
import AvatarUpload from "../components/Avatar/AvatarUpload";
import { useAppSelector, useAppDispatch } from "../store/hooks";
import { setUser } from "../store/slices/authSlice";
import api from "../services/api";
import { API_BASE_URL } from "../config/api";
import { formatDate } from "../utils/dateUtils";
import {
  Tabs as UiTabs,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import {
  TooltipProvider,
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipTrigger,
} from "../components/ui/tooltip";
import { Loader2, Trash2, Search, CheckCircle2, AlertTriangle } from "lucide-react";
import {
  Card as UiCard,
  CardHeader as UiCardHeader,
  CardTitle,
  CardDescription,
  CardContent as UiCardContent,
} from "../components/ui/card";
import { Button as UiButton } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch as UiSwitch } from "../components/ui/switch";
import { Separator } from "../components/ui/separator";
import {
  Alert as UiAlert,
  AlertDescription,
  AlertTitle,
} from "../components/ui/alert";
import {
  Table as UiTable,
  TableBody as UiTableBody,
  TableCell as UiTableCell,
  TableHead as UiTableHead,
  TableHeader as UiTableHeader,
  TableRow as UiTableRow,
} from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog as UiDialog,
  DialogContent as UiDialogContent,
  DialogDescription as UiDialogDescription,
  DialogFooter as UiDialogFooter,
  DialogHeader as UiDialogHeader,
  DialogTitle as UiDialogTitle,
} from "../components/ui/dialog";
import {
  Avatar as ScnAvatar,
  AvatarFallback as ScnAvatarFallback,
} from "../components/ui/avatar";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && (
        <div className="px-3 py-4 sm:px-5 md:px-8 md:py-6">{children}</div>
      )}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useAppSelector((state) => state.auth);

  // Parse tab from URL query parameters
  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    return tabParam ? parseInt(tabParam, 10) : 0;
  };

  const [selectedTab, setSelectedTab] = useState(getInitialTab());
  const [profileSubTab, setProfileSubTab] = useState(0); // For My Profile sub-tabs
  const [profileEditing, setProfileEditing] = useState(false); // For editing state in profile
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [auditLogDetailOpen, setAuditLogDetailOpen] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState<any>(null);
  const [contactSupportOpen, setContactSupportOpen] = useState(false);
  const [scheduleDemoOpen, setScheduleDemoOpen] = useState(false);
  const [slaData, setSlaData] = useState<any[]>([]);
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditSearchQuery, setAuditSearchQuery] = useState("");
  const [auditDateFilter, setAuditDateFilter] = useState("");
  const [auditActionFilter, setAuditActionFilter] = useState("all");

  const filteredAuditLogs = useMemo(() => {
    return auditLogs.filter((log) => {
      const q = auditSearchQuery.trim().toLowerCase();
      if (q) {
        const blob = `${log.user} ${log.action} ${log.ip} ${log.details ?? ""}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (auditDateFilter) {
        const d = new Date(log.timestamp);
        const pad = (n: number) => String(n).padStart(2, "0");
        const local = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
        if (local !== auditDateFilter) return false;
      }
      if (auditActionFilter !== "all") {
        const a = String(log.action).toLowerCase();
        if (auditActionFilter === "login" && !a.includes("login")) return false;
        if (auditActionFilter === "api" && !a.includes("api")) return false;
        if (auditActionFilter === "settings" && !a.includes("settings"))
          return false;
        if (auditActionFilter === "tokens" && !a.includes("token"))
          return false;
      }
      return true;
    });
  }, [auditLogs, auditSearchQuery, auditDateFilter, auditActionFilter]);

  const [teamPerformanceData, setTeamPerformanceData] = useState<any[]>([]);
  const [performanceDateRange, setPerformanceDateRange] = useState<
    "7days" | "30days" | "custom"
  >("7days");
  const [customDateRange] = useState({
    start: "",
    end: "",
  });
  const [slaConfigDialogOpen, setSlaConfigDialogOpen] = useState(false);
  const [editingSlaField, setEditingSlaField] = useState<string>("");
  const [slaConfig, setSlaConfig] = useState({
    firstResponseTime: "5",
    resolutionTime: "2",
    csatTarget: "90",
    workingHours: "9 AM - 6 PM (UTC)",
  });

  // API Token states
  const [apiTokens, setApiTokens] = useState<any[]>([]);
  const [tokenLoading, setTokenLoading] = useState(false);
  const [showCreateToken, setShowCreateToken] = useState(false);
  const [currentToken, setCurrentToken] = useState("");
  const [tokenName, setTokenName] = useState("");
  const [tokenExpiry, setTokenExpiry] = useState("30");
  const [showTokenValue, setShowTokenValue] = useState(false);

  const [systemInfo, setSystemInfo] = useState({
    version: "",
    instanceId: "",
    region: "",
    status: "loading",
  });

  const [accountData, setAccountData] = useState({
    name: "",
    domain: "",
    supportEmail: "",
    locale: "en",
    timezone: "UTC",
    autoResolve: true,
    emailNotifications: true,
    inactivityTimeout: 30,
    workingHours: "9 AM - 6 PM",
  });

  const [, setOriginalAccountData] = useState({});

  // Update tab when URL changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get("tab");
    if (tabParam) {
      const tabIndex = parseInt(tabParam, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 7) {
        setSelectedTab(tabIndex);
      }
    }
  }, [location.search]);

  // Agent profile state
  const [agentProfileData, setAgentProfileData] = useState({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    display_name: "",
    email: user?.email || "",
    phone_number: "",
    avatar_url: user?.avatar_url || "",
    timezone: "UTC",
    language: "en",
  });

  const [profileSaving, setProfileSaving] = useState(false);

  // Personal Info state for My Profile tab
  const [personalInfo, setPersonalInfo] = useState({
    name: user?.full_name || "",
    email: user?.email || "",
    phone: "",
    location: "",
  });

  // Company Details state for My Profile tab
  const [companyDetails, setCompanyDetails] = useState({
    companyName: "",
    industry: "",
    companySize: "",
    website: "",
  });

  // Password change state for My Profile tab
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Load agent profile when user changes
  useEffect(() => {
    if (user) {
      setAgentProfileData({
        first_name: user.first_name || "",
        last_name: user.last_name || "",
        display_name: "",
        email: user.email || "",
        phone_number: "",
        avatar_url: user.avatar_url || "",
        timezone: "UTC",
        language: "en",
      });
      loadCurrentAgentProfile();
    }
  }, [user?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load data on component mount
  useEffect(() => {
    loadAccountData();
    loadSLAData();
    loadAuditLogs();
    loadSystemInfo();
    loadApiTokens();
    loadTeamPerformance();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload SLA data when date range changes
  useEffect(() => {
    if (selectedTab === 4) {
      // Only reload when on SLA & Performance tab
      loadSLAData();
      loadTeamPerformance();
    }
  }, [performanceDateRange, customDateRange]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load current agent's full profile
  const loadCurrentAgentProfile = async () => {
    if (!user?.id) return;

    try {
      const response = await api.get(`/agents/${user.id}`);
      const agent = response.data;

      setAgentProfileData({
        first_name: agent.first_name || "",
        last_name: agent.last_name || "",
        display_name: agent.display_name || "",
        email: agent.email || "",
        phone_number: agent.phone_number || "",
        avatar_url: agent.avatar_url || "",
        timezone: agent.timezone || "UTC",
        language: agent.language || "en",
      });
    } catch (error) {
      console.error("Failed to load agent profile:", error);
    }
  };

  // Save agent profile (ready for future profile editor wiring)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- handler preserved; not yet bound in UI
  const handleSaveAgentProfile = async () => {
    if (!user?.id) return;

    setProfileSaving(true);
    try {
      await api.patch(`/agents/${user.id}`, agentProfileData);

      // Update Redux store with new user data
      dispatch(
        setUser({
          ...user,
          first_name: agentProfileData.first_name,
          last_name: agentProfileData.last_name,
          full_name: `${agentProfileData.first_name} ${agentProfileData.last_name}`,
          email: agentProfileData.email,
          avatar_url: agentProfileData.avatar_url,
        }),
      );

      setSnackbarMessage("Profile updated successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Failed to save agent profile:", error);
      setSnackbarMessage("Failed to save profile. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setProfileSaving(false);
    }
  };

  // Handle Personal Info Save
  const handlePersonalInfoSave = async () => {
    setProfileSaving(true);
    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setSnackbarMessage("Personal information updated successfully!");
      setSnackbarOpen(true);
      setProfileEditing(false);
    } catch (error) {
      console.error("Failed to save personal info:", error);
      setSnackbarMessage("Failed to save personal information.");
      setSnackbarOpen(true);
    } finally {
      setProfileSaving(false);
    }
  };

  // Handle Password Change
  const handlePasswordChange = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setSnackbarMessage("New passwords do not match!");
      setSnackbarOpen(true);
      return;
    }

    if (passwordData.newPassword.length < 8) {
      setSnackbarMessage("Password must be at least 8 characters long!");
      setSnackbarOpen(true);
      return;
    }

    setProfileSaving(true);
    try {
      await api.post("/password/change-password", {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });

      setSnackbarMessage("Password changed successfully!");
      setSnackbarOpen(true);
      setPasswordData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
    } catch (error: any) {
      console.error("Failed to change password:", error);
      setSnackbarMessage(
        error.response?.data?.detail || "Failed to change password.",
      );
      setSnackbarOpen(true);
    } finally {
      setProfileSaving(false);
    }
  };

  // Load existing API tokens
  const loadApiTokens = async () => {
    setTokenLoading(true);
    try {
      const accessToken = localStorage.getItem("token");
      console.log(
        "🔐 Access token for API tokens request:",
        accessToken ? `${accessToken.substring(0, 20)}...` : "NOT FOUND",
      );

      if (!accessToken) {
        console.error("❌ No access token found in localStorage");
        setApiTokens([]);
        setTokenLoading(false);
        return;
      }

      const response = await fetch(
        `${API_BASE_URL}/api-tokens/`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      console.log("📡 API tokens request response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("✅ Loaded API tokens from backend:", data);
        setApiTokens(data || []); // Fixed: data is the array directly, not data.tokens
      } else {
        console.error(
          "❌ Failed to load API tokens from backend:",
          response.status,
        );
        const errorText = await response.text();
        console.error("❌ Error details:", errorText);
        setApiTokens([]); // Don't show demo data - force real backend integration
      }
    } catch (error) {
      console.error("❌ Error loading API tokens:", error);
      setApiTokens([]); // Don't show demo data - force real backend integration
    } finally {
      setTokenLoading(false);
    }
  };

  // Generate new API token
  const generateApiToken = async () => {
    if (!tokenName.trim()) {
      setSnackbarMessage("Please provide a token name");
      setSnackbarOpen(true);
      return;
    }

    setTokenLoading(true);
    try {
      const accessToken = localStorage.getItem("token");
      console.log(
        "🔐 Access token for token creation:",
        accessToken ? `${accessToken.substring(0, 20)}...` : "NOT FOUND",
      );

      if (!accessToken) {
        throw new Error("No access token found. Please login again.");
      }

      console.log("📝 Creating API token via backend...");

      // Call the proper API token creation endpoint
      const response = await fetch(
        `${API_BASE_URL}/api-tokens/`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({
            name: tokenName,
            expires_in_days: parseInt(tokenExpiry),
            description: `Generated from web UI on ${new Date().toLocaleDateString()}`,
          }),
        },
      );

      console.log("📡 API token creation response status:", response.status);

      if (!response.ok) {
        const errorText = await response.text();
        console.error(
          "❌ API token creation failed:",
          response.status,
          errorText,
        );
        throw new Error(
          errorText || `Failed to create API token (${response.status})`,
        );
      }

      const data = await response.json();
      console.log("✅ API token created successfully:", data);

      // Store the new token for display
      setCurrentToken(data.access_token);
      setShowTokenValue(true);

      // Clear form
      setTokenName("");
      setShowCreateToken(false);

      // Refresh the tokens list from backend to show the newly created token
      await loadApiTokens();

      setSnackbarMessage(
        "API token generated successfully and stored in database! Copy it now - you won't see it again.",
      );
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error generating API token:", error);
      const errorMessage = formatApiTokenError(error, "generate");
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    } finally {
      setTokenLoading(false);
    }
  };

  // Copy token to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setSnackbarMessage("Copied to clipboard!");
      setSnackbarOpen(true);
    } catch (error) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setSnackbarMessage("Copied to clipboard!");
      setSnackbarOpen(true);
    }
  };

  // Revoke API token
  const revokeApiToken = async (tokenId: number) => {
    if (
      !window.confirm(
        "Are you sure you want to revoke this token? This action cannot be undone.",
      )
    ) {
      return;
    }

    try {
      console.log("Revoking API token via backend:", tokenId);

      const response = await fetch(
        `${API_BASE_URL}/api-tokens/${tokenId}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        },
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || "Failed to revoke token");
      }

      console.log("API token revoked successfully in backend");

      // Refresh the tokens list from backend
      await loadApiTokens();

      setSnackbarMessage("API token revoked successfully in database");
      setSnackbarOpen(true);
    } catch (error) {
      console.error("Error revoking API token:", error);
      const errorMessage = formatApiTokenError(error, "revoke");
      setSnackbarMessage(errorMessage);
      setSnackbarOpen(true);
    }
  };

  const loadAccountData = async () => {
    try {
      const savedData = localStorage.getItem("converse_account_settings");
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        setAccountData(parsedData);
        setOriginalAccountData(parsedData);
      } else {
        const defaultData = {
          name: "Acme Corp",
          domain: "acme.converse.com",
          supportEmail: "support@acme.com",
          locale: "en",
          timezone: "UTC",
          autoResolve: true,
          emailNotifications: true,
          inactivityTimeout: 30,
          workingHours: "9 AM - 6 PM",
        };
        setAccountData(defaultData);
        setOriginalAccountData(defaultData);
      }
    } catch (error) {
      console.error("Failed to load account data:", error);
    }
  };

  const loadSLAData = async () => {
    setLoading(true);
    try {
      // Calculate date range based on selection
      const endDate = new Date();
      const startDate = new Date();

      if (performanceDateRange === "7days") {
        startDate.setDate(endDate.getDate() - 7);
      } else if (performanceDateRange === "30days") {
        startDate.setDate(endDate.getDate() - 30);
      } else if (customDateRange.start && customDateRange.end) {
        startDate.setTime(new Date(customDateRange.start).getTime());
        endDate.setTime(new Date(customDateRange.end).getTime());
      }

      const startDateStr = startDate.toISOString().split("T")[0];
      const endDateStr = endDate.toISOString().split("T")[0];

      const response = await api.get(
        `/advanced-reports/sla-compliance?start_date=${startDateStr}&end_date=${endDateStr}`,
      );

      if (response.data) {
        // Transform backend data to frontend format
        const transformed = [
          {
            metric: "First Response Time",
            target: `< ${slaConfig.firstResponseTime} min`,
            current: `${response.data.avg_first_response_time?.toFixed(1) || 0} min`,
            status:
              (response.data.first_response_sla_compliance || 0) >= 95
                ? "excellent"
                : (response.data.first_response_sla_compliance || 0) >= 80
                  ? "good"
                  : "needs-improvement",
            percentage: Math.round(
              response.data.first_response_sla_compliance || 0,
            ),
          },
          {
            metric: "Resolution Time",
            target: `< ${slaConfig.resolutionTime} hrs`,
            current: `${response.data.avg_resolution_time?.toFixed(1) || 0} hrs`,
            status:
              (response.data.resolution_sla_compliance || 0) >= 90
                ? "excellent"
                : (response.data.resolution_sla_compliance || 0) >= 75
                  ? "good"
                  : "needs-improvement",
            percentage: Math.round(
              response.data.resolution_sla_compliance || 0,
            ),
          },
          {
            metric: "Customer Satisfaction",
            target: `> ${slaConfig.csatTarget}%`,
            current: `${response.data.average_csat_score?.toFixed(0) || 0}%`,
            status:
              (response.data.average_csat_score || 0) >= 90
                ? "excellent"
                : (response.data.average_csat_score || 0) >= 75
                  ? "good"
                  : "needs-improvement",
            percentage: Math.round(response.data.average_csat_score || 0),
          },
          {
            metric: "Response Rate",
            target: "> 95%",
            current: `${response.data.response_rate?.toFixed(0) || 0}%`,
            status:
              (response.data.response_rate || 0) >= 95
                ? "excellent"
                : (response.data.response_rate || 0) >= 85
                  ? "good"
                  : "needs-improvement",
            percentage: Math.round(response.data.response_rate || 0),
          },
        ];
        setSlaData(transformed);
      }
    } catch (error) {
      console.error("Failed to load SLA data:", error);
      // Fallback to default values if API fails
      setSlaData([
        {
          metric: "First Response Time",
          target: "< 5 min",
          current: "N/A",
          status: "needs-improvement",
          percentage: 0,
        },
        {
          metric: "Resolution Time",
          target: "< 2 hrs",
          current: "N/A",
          status: "needs-improvement",
          percentage: 0,
        },
        {
          metric: "Customer Satisfaction",
          target: "> 90%",
          current: "N/A",
          status: "needs-improvement",
          percentage: 0,
        },
        {
          metric: "Response Rate",
          target: "> 95%",
          current: "N/A",
          status: "needs-improvement",
          percentage: 0,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTeamPerformance = async () => {
    try {
      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();

      if (performanceDateRange === "7days") {
        startDate.setDate(endDate.getDate() - 7);
      } else if (performanceDateRange === "30days") {
        startDate.setDate(endDate.getDate() - 30);
      } else if (customDateRange.start && customDateRange.end) {
        startDate.setTime(new Date(customDateRange.start).getTime());
        endDate.setTime(new Date(customDateRange.end).getTime());
      }

      const startDateStr = startDate.toISOString().split("T")[0];
      const endDateStr = endDate.toISOString().split("T")[0];

      const response = await api.get(
        `/advanced-reports/team-analytics?start_date=${startDateStr}&end_date=${endDateStr}`,
      );

      if (response.data && response.data.team_performance) {
        const transformedData = response.data.team_performance.map(
          (member: any) => ({
            name: member.agent_name || "Unknown",
            conversations: member.conversations_handled || 0,
            responseTime: `${member.avg_response_time?.toFixed(1) || 0} min`,
            resolutionTime: `${member.avg_resolution_time?.toFixed(1) || 0} hrs`,
            rating: member.csat_score?.toFixed(1) || 0,
            status:
              (member.csat_score || 0) >= 4.5
                ? "excellent"
                : (member.csat_score || 0) >= 4.0
                  ? "good"
                  : "needs-improvement",
          }),
        );
        setTeamPerformanceData(transformedData);
      }
    } catch (error) {
      console.error("Failed to load team performance:", error);
      // Fallback data
      setTeamPerformanceData([]);
    }
  };

  const loadAuditLogs = async () => {
    try {
      await new Promise((resolve) => setTimeout(resolve, 800));
      setAuditLogs([
        {
          id: 1,
          action: "User Login",
          user: "John Doe",
          timestamp: new Date().toISOString(),
          ip: "192.168.1.1",
          status: "success",
          details: "Successful login from Chrome browser",
        },
        {
          id: 2,
          action: "API Token Generated",
          user: "Admin User",
          timestamp: new Date(Date.now() - 300000).toISOString(),
          ip: "192.168.1.2",
          status: "success",
          details: "New API token created: MCP Integration",
        },
        {
          id: 3,
          action: "Settings Updated",
          user: "Admin User",
          timestamp: new Date(Date.now() - 600000).toISOString(),
          ip: "192.168.1.3",
          status: "success",
          details: "Account settings updated: notification preferences changed",
        },
      ]);
    } catch (error) {
      console.error("Failed to load audit logs:", error);
    }
  };

  // Handler for date range button clicks
  const handleDateRangeChange = (range: "7days" | "30days" | "custom") => {
    if (range === "custom") {
      // For custom range, we'll need to open a date picker dialog
      // For now, just set it and user can modify customDateRange state
      setPerformanceDateRange(range);
    } else {
      setPerformanceDateRange(range);
    }
  };

  // Handler for SLA configuration edit
  const handleSLAConfigEdit = (field: string) => {
    setEditingSlaField(field);
    setSlaConfigDialogOpen(true);
  };

  // Handler for saving SLA configuration
  const handleSaveSLAConfig = async () => {
    try {
      setSaving(true);
      // In a real app, save to backend
      // await api.post('/settings/sla-config', slaConfig);
      setSlaConfigDialogOpen(false);
      setSnackbarMessage("SLA configuration updated successfully");
      setSnackbarOpen(true);
      // Reload SLA data with new config
      loadSLAData();
    } catch (error) {
      console.error("Failed to save SLA config:", error);
      setSnackbarMessage("Failed to save SLA configuration");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  const loadSystemInfo = async () => {
    try {
      const version = `v${Math.floor(Math.random() * 3) + 2}.${Math.floor(Math.random() * 20)}.${Math.floor(Math.random() * 10)}`;
      const instanceId = `conv-${Math.random().toString(36).substr(2, 9)}`;
      const regions = ["US East", "US West", "Europe", "Asia Pacific"];
      const region = regions[Math.floor(Math.random() * regions.length)];

      setSystemInfo({
        version,
        instanceId,
        region,
        status: "operational",
      });
    } catch (error) {
      console.error("Failed to load system info:", error);
      setSystemInfo((prev) => ({ ...prev, status: "error" }));
    }
  };

  const handleSaveSettings = async () => {
    setSaving(true);
    try {
      localStorage.setItem(
        "converse_account_settings",
        JSON.stringify(accountData),
      );
      await new Promise((resolve) => setTimeout(resolve, 1500));
      setOriginalAccountData(accountData);
      setSnackbarMessage("Settings saved successfully!");
      setSnackbarOpen(true);
    } catch (error) {
      setSnackbarMessage("Failed to save settings. Please try again.");
      setSnackbarOpen(true);
    } finally {
      setSaving(false);
    }
  };

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex min-h-[calc(100vh-120px)] flex-col bg-slate-50/95 pb-6 text-foreground">
        <div className="mx-auto flex w-full max-w-7xl flex-1 flex-col px-3 sm:px-4 md:px-6 lg:px-8">
          <header className="shrink-0 pb-4 pt-4 md:pb-6 md:pt-6">
            <h1 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
              Settings
            </h1>
            <p className="mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
              Manage your profile, workspace account, API access, performance
              insights, integrations, and support from one place.
            </p>
          </header>

          <UiTabs
            value={String(selectedTab)}
            onValueChange={(v) => setSelectedTab(parseInt(v, 10))}
            className="flex min-h-0 flex-1 flex-col gap-0"
          >
            <div className="shrink-0 overflow-x-auto rounded-t-2xl border border-b-0 border-border/70 bg-muted/40 px-2 pt-2 sm:px-3">
              <TabsList className="mb-0 inline-flex h-auto min-h-11 w-max min-w-full flex-wrap justify-start gap-1 bg-transparent p-0 sm:flex-nowrap md:gap-0.5">
                <TabsTrigger
                  value="0"
                  className="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:text-sm"
                >
                  <Person sx={{ fontSize: "1.1rem" }} />
                  <span className="whitespace-nowrap">My Profile</span>
                </TabsTrigger>
                <TabsTrigger
                  value="1"
                  className="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:text-sm"
                >
                  <AccountCircle sx={{ fontSize: "1.1rem" }} />
                  <span className="whitespace-nowrap">Account</span>
                </TabsTrigger>
                <TabsTrigger
                  value="2"
                  className="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:text-sm"
                >
                  <Key sx={{ fontSize: "1.1rem" }} />
                  <span className="whitespace-nowrap">API Tokens</span>
                </TabsTrigger>
                <TabsTrigger
                  value="3"
                  className="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:text-sm"
                >
                  <History sx={{ fontSize: "1.1rem" }} />
                  <span className="whitespace-nowrap">Audit Logs</span>
                </TabsTrigger>
                <TabsTrigger
                  value="4"
                  className="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:text-sm"
                >
                  <Speed sx={{ fontSize: "1.1rem" }} />
                  <span className="whitespace-nowrap">SLA & Performance</span>
                </TabsTrigger>
                <TabsTrigger
                  value="5"
                  className="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:text-sm"
                >
                  <Help sx={{ fontSize: "1.1rem" }} />
                  <span className="whitespace-nowrap">Help & Support</span>
                </TabsTrigger>
                <TabsTrigger
                  value="6"
                  className="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:text-sm"
                >
                  <Psychology sx={{ fontSize: "1.1rem" }} />
                  <span className="whitespace-nowrap">Co-Pilot</span>
                </TabsTrigger>
                <TabsTrigger
                  value="7"
                  className="flex shrink-0 items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:text-sm"
                >
                  <SmartToy sx={{ fontSize: "1.1rem" }} />
                  <span className="whitespace-nowrap">AI Configuration</span>
                </TabsTrigger>
              </TabsList>
            </div>

            <Box
              sx={{ flex: 1, overflow: "auto" }}
              className="min-h-0 flex-1 overflow-y-auto rounded-b-2xl border border-border/70 bg-card shadow-sm"
            >
          {/* My Profile Tab (index 0) - Full multi-tab profile management */}
          <TabPanel value={selectedTab} index={0}>
            <UiCard className="mb-6 overflow-hidden rounded-2xl border-border/60 bg-gradient-to-br from-card via-card to-muted/25 shadow-sm">
              <UiCardContent className="p-6 md:p-8">
                <div className="flex flex-col items-center gap-6 sm:flex-row sm:items-start">
                  <div className="relative shrink-0">
                    <AvatarUpload
                      currentAvatar={agentProfileData.avatar_url}
                      onAvatarChange={(url) =>
                        setAgentProfileData((prev) => ({
                          ...prev,
                          avatar_url: url || "",
                        }))
                      }
                      name={`${agentProfileData.first_name} ${agentProfileData.last_name}`.trim()}
                      size={100}
                    />
                  </div>
                  <div className="min-w-0 flex-1 space-y-3 text-center sm:text-left">
                    <div>
                      <h2 className="text-2xl font-semibold tracking-tight text-foreground md:text-3xl">
                        {user?.full_name || "User Name"}
                      </h2>
                      <p className="mt-1 text-sm text-muted-foreground md:text-base">
                        {user?.email}
                      </p>
                    </div>
                    <div className="flex flex-wrap justify-center gap-2 sm:justify-start">
                      <Badge variant="default" className="font-medium">
                        {user?.role?.replace("_", " ").toUpperCase() || "Agent"}
                      </Badge>
                      <Badge variant="success">Active</Badge>
                    </div>
                  </div>
                </div>
              </UiCardContent>
            </UiCard>

            <UiTabs
              value={String(profileSubTab)}
              onValueChange={(v) => setProfileSubTab(parseInt(v, 10))}
              className="mb-6 w-full"
            >
              <div className="shrink-0 overflow-x-auto rounded-xl border border-border/70 bg-muted/40 px-2 py-2 sm:px-3">
                <TabsList className="mb-0 inline-flex h-auto min-h-10 w-max min-w-full flex-nowrap justify-start gap-1 bg-transparent p-0 sm:gap-0.5">
                  <TabsTrigger
                    value="0"
                    className="flex min-w-[8.5rem] shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:min-w-0 sm:flex-1 sm:text-sm"
                  >
                    <Person sx={{ fontSize: "1.05rem", opacity: 0.85 }} />
                    <span className="whitespace-nowrap">Personal Info</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="1"
                    className="flex min-w-[8.5rem] shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:min-w-0 sm:flex-1 sm:text-sm"
                  >
                    <Business sx={{ fontSize: "1.05rem", opacity: 0.85 }} />
                    <span className="whitespace-nowrap">Company Details</span>
                  </TabsTrigger>
                  <TabsTrigger
                    value="2"
                    className="flex min-w-[8.5rem] shrink-0 items-center justify-center gap-2 rounded-lg px-3 py-2 text-xs font-medium sm:min-w-0 sm:flex-1 sm:text-sm"
                  >
                    <Security sx={{ fontSize: "1.05rem", opacity: 0.85 }} />
                    <span className="whitespace-nowrap">Security</span>
                  </TabsTrigger>
                </TabsList>
              </div>
            </UiTabs>

            {profileSubTab === 0 && (
              <UiCard className="rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="flex flex-col gap-4 space-y-0 border-b border-border/60 pb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">Personal Information</CardTitle>
                    <CardDescription>
                      Details shown on your profile; use Edit to make changes.
                    </CardDescription>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {!profileEditing ? (
                      <UiButton
                        variant="outline"
                        onClick={() => setProfileEditing(true)}
                      >
                        <Edit sx={{ fontSize: "1.125rem" }} />
                        Edit
                      </UiButton>
                    ) : (
                      <>
                        <UiButton
                          onClick={handlePersonalInfoSave}
                          disabled={profileSaving}
                        >
                          {profileSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save sx={{ fontSize: "1.125rem" }} />
                          )}
                          {profileSaving ? "Saving..." : "Save"}
                        </UiButton>
                        <UiButton
                          variant="outline"
                          onClick={() => setProfileEditing(false)}
                        >
                          <Cancel sx={{ fontSize: "1.125rem" }} />
                          Cancel
                        </UiButton>
                      </>
                    )}
                  </div>
                </UiCardHeader>
                <UiCardContent className="grid gap-4 pt-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="profile-personal-name">Full Name</Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 text-muted-foreground">
                        <Person sx={{ fontSize: "1.125rem" }} />
                      </span>
                      <Input
                        id="profile-personal-name"
                        className="pl-9"
                        value={personalInfo.name}
                        onChange={(e) =>
                          setPersonalInfo({
                            ...personalInfo,
                            name: e.target.value,
                          })
                        }
                        disabled={!profileEditing}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-personal-email">Email</Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 text-muted-foreground">
                        <Email sx={{ fontSize: "1.125rem" }} />
                      </span>
                      <Input
                        id="profile-personal-email"
                        type="email"
                        className="pl-9"
                        value={personalInfo.email}
                        onChange={(e) =>
                          setPersonalInfo({
                            ...personalInfo,
                            email: e.target.value,
                          })
                        }
                        disabled={!profileEditing}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-personal-phone">Phone</Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 text-muted-foreground">
                        <Phone sx={{ fontSize: "1.125rem" }} />
                      </span>
                      <Input
                        id="profile-personal-phone"
                        className="pl-9"
                        value={personalInfo.phone}
                        onChange={(e) =>
                          setPersonalInfo({
                            ...personalInfo,
                            phone: e.target.value,
                          })
                        }
                        disabled={!profileEditing}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-personal-location">Location</Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 text-muted-foreground">
                        <LocationOn sx={{ fontSize: "1.125rem" }} />
                      </span>
                      <Input
                        id="profile-personal-location"
                        className="pl-9"
                        value={personalInfo.location}
                        onChange={(e) =>
                          setPersonalInfo({
                            ...personalInfo,
                            location: e.target.value,
                          })
                        }
                        disabled={!profileEditing}
                      />
                    </div>
                  </div>
                </UiCardContent>
              </UiCard>
            )}

            {profileSubTab === 1 && (
              <UiCard className="rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="flex flex-col gap-4 space-y-0 border-b border-border/60 pb-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="space-y-1">
                    <CardTitle className="text-lg">Company Details</CardTitle>
                    <CardDescription>
                      Optional organization context for your workspace profile.
                    </CardDescription>
                  </div>
                  <div className="flex shrink-0 flex-wrap gap-2">
                    {!profileEditing ? (
                      <UiButton
                        variant="outline"
                        onClick={() => setProfileEditing(true)}
                      >
                        <Edit sx={{ fontSize: "1.125rem" }} />
                        Edit
                      </UiButton>
                    ) : (
                      <>
                        <UiButton
                          onClick={handlePersonalInfoSave}
                          disabled={profileSaving}
                        >
                          {profileSaving ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <Save sx={{ fontSize: "1.125rem" }} />
                          )}
                          {profileSaving ? "Saving..." : "Save"}
                        </UiButton>
                        <UiButton
                          variant="outline"
                          onClick={() => setProfileEditing(false)}
                        >
                          <Cancel sx={{ fontSize: "1.125rem" }} />
                          Cancel
                        </UiButton>
                      </>
                    )}
                  </div>
                </UiCardHeader>
                <UiCardContent className="grid gap-4 pt-6 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="profile-company-name">Company Name</Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 text-muted-foreground">
                        <Business sx={{ fontSize: "1.125rem" }} />
                      </span>
                      <Input
                        id="profile-company-name"
                        className="pl-9"
                        value={companyDetails.companyName}
                        onChange={(e) =>
                          setCompanyDetails({
                            ...companyDetails,
                            companyName: e.target.value,
                          })
                        }
                        disabled={!profileEditing}
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-company-industry">Industry</Label>
                    <Input
                      id="profile-company-industry"
                      value={companyDetails.industry}
                      onChange={(e) =>
                        setCompanyDetails({
                          ...companyDetails,
                          industry: e.target.value,
                        })
                      }
                      disabled={!profileEditing}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-company-size">Company Size</Label>
                    <Input
                      id="profile-company-size"
                      value={companyDetails.companySize}
                      onChange={(e) =>
                        setCompanyDetails({
                          ...companyDetails,
                          companySize: e.target.value,
                        })
                      }
                      disabled={!profileEditing}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="profile-company-website">Website</Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 text-muted-foreground">
                        <Web sx={{ fontSize: "1.125rem" }} />
                      </span>
                      <Input
                        id="profile-company-website"
                        className="pl-9"
                        value={companyDetails.website}
                        onChange={(e) =>
                          setCompanyDetails({
                            ...companyDetails,
                            website: e.target.value,
                          })
                        }
                        disabled={!profileEditing}
                      />
                    </div>
                  </div>
                </UiCardContent>
              </UiCard>
            )}

            {profileSubTab === 2 && (
              <UiCard className="rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="space-y-1 border-b border-border/60 pb-4">
                  <CardTitle className="text-lg">Change Password</CardTitle>
                  <CardDescription>
                    Ensure your password is strong and unique to protect your
                    account.
                  </CardDescription>
                </UiCardHeader>
                <UiCardContent className="space-y-4 pt-6">
                  <div className="space-y-2">
                    <Label htmlFor="profile-pw-current">Current Password</Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 z-10 flex -translate-y-1/2 text-muted-foreground">
                        <Key sx={{ fontSize: "1.125rem" }} />
                      </span>
                      <Input
                        id="profile-pw-current"
                        className="pl-9 pr-11"
                        type={showCurrentPassword ? "text" : "password"}
                        autoComplete="current-password"
                        value={passwordData.currentPassword}
                        onChange={(e) =>
                          setPasswordData({
                            ...passwordData,
                            currentPassword: e.target.value,
                          })
                        }
                      />
                      <UiButton
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground"
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        aria-label={
                          showCurrentPassword ? "Hide password" : "Show password"
                        }
                      >
                        {showCurrentPassword ? (
                          <VisibilityOff sx={{ fontSize: "1.125rem" }} />
                        ) : (
                          <Visibility sx={{ fontSize: "1.125rem" }} />
                        )}
                      </UiButton>
                    </div>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor="profile-pw-new">New Password</Label>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 z-10 flex -translate-y-1/2 text-muted-foreground">
                          <Key sx={{ fontSize: "1.125rem" }} />
                        </span>
                        <Input
                          id="profile-pw-new"
                          className="pl-9 pr-11"
                          type={showNewPassword ? "text" : "password"}
                          autoComplete="new-password"
                          value={passwordData.newPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              newPassword: e.target.value,
                            })
                          }
                        />
                        <UiButton
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          aria-label={
                            showNewPassword ? "Hide password" : "Show password"
                          }
                        >
                          {showNewPassword ? (
                            <VisibilityOff sx={{ fontSize: "1.125rem" }} />
                          ) : (
                            <Visibility sx={{ fontSize: "1.125rem" }} />
                          )}
                        </UiButton>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="profile-pw-confirm">Confirm New Password</Label>
                      <div className="relative">
                        <span className="pointer-events-none absolute left-3 top-1/2 z-10 flex -translate-y-1/2 text-muted-foreground">
                          <Key sx={{ fontSize: "1.125rem" }} />
                        </span>
                        <Input
                          id="profile-pw-confirm"
                          className="pl-9 pr-11"
                          type={showConfirmPassword ? "text" : "password"}
                          autoComplete="new-password"
                          value={passwordData.confirmPassword}
                          onChange={(e) =>
                            setPasswordData({
                              ...passwordData,
                              confirmPassword: e.target.value,
                            })
                          }
                        />
                        <UiButton
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground"
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          aria-label={
                            showConfirmPassword
                              ? "Hide password"
                              : "Show password"
                          }
                        >
                          {showConfirmPassword ? (
                            <VisibilityOff sx={{ fontSize: "1.125rem" }} />
                          ) : (
                            <Visibility sx={{ fontSize: "1.125rem" }} />
                          )}
                        </UiButton>
                      </div>
                    </div>
                  </div>
                  <UiAlert className="rounded-xl">
                    <AlertDescription>
                      Password must be at least 8 characters long and include
                      uppercase, lowercase, numbers, and special characters.
                    </AlertDescription>
                  </UiAlert>
                  <UiButton
                    size="lg"
                    className="w-full sm:w-auto"
                    onClick={handlePasswordChange}
                    disabled={profileSaving}
                  >
                    {profileSaving ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Save sx={{ fontSize: "1.125rem" }} />
                    )}
                    {profileSaving
                      ? "Changing Password..."
                      : "Change Password"}
                  </UiButton>
                </UiCardContent>
              </UiCard>
            )}
          </TabPanel>

          {/* Account Settings Tab (now index 1) */}
          <TabPanel value={selectedTab} index={1}>
            <div className="grid gap-6 md:grid-cols-2">
              <UiCard className="rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="flex flex-row items-start gap-3 space-y-0 pb-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Business sx={{ fontSize: "1.25rem" }} />
                  </div>
                  <div className="min-w-0 space-y-1.5">
                    <CardTitle className="text-base">General Information</CardTitle>
                    <CardDescription>
                      Workspace identity used across your Converse account.
                    </CardDescription>
                  </div>
                </UiCardHeader>
                <UiCardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="settings-account-name">Account Name</Label>
                    <Input
                      id="settings-account-name"
                      value={accountData.name}
                      onChange={(e) =>
                        setAccountData({
                          ...accountData,
                          name: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="settings-account-domain">Domain</Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center text-muted-foreground">
                        <Web sx={{ fontSize: "1.125rem" }} />
                      </span>
                      <Input
                        id="settings-account-domain"
                        className="pl-9"
                        value={accountData.domain}
                        onChange={(e) =>
                          setAccountData({
                            ...accountData,
                            domain: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="settings-account-support-email">
                      Support Email
                    </Label>
                    <div className="relative">
                      <span className="pointer-events-none absolute left-3 top-1/2 flex -translate-y-1/2 items-center text-muted-foreground">
                        <Email sx={{ fontSize: "1.125rem" }} />
                      </span>
                      <Input
                        id="settings-account-support-email"
                        type="email"
                        className="pl-9"
                        value={accountData.supportEmail}
                        onChange={(e) =>
                          setAccountData({
                            ...accountData,
                            supportEmail: e.target.value,
                          })
                        }
                      />
                    </div>
                  </div>
                </UiCardContent>
              </UiCard>

              <UiCard className="rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="flex flex-row items-start gap-3 space-y-0 pb-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <NotificationsActive sx={{ fontSize: "1.25rem" }} />
                  </div>
                  <div className="min-w-0 space-y-1.5">
                    <CardTitle className="text-base">Preferences</CardTitle>
                    <CardDescription>
                      Defaults for how your workspace handles conversations and
                      alerts.
                    </CardDescription>
                  </div>
                </UiCardHeader>
                <UiCardContent className="space-y-0">
                  <div className="flex items-start justify-between gap-4 py-3">
                    <div className="min-w-0 space-y-1 pr-2">
                      <p className="text-sm font-medium leading-none">
                        Auto-resolve conversations
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Automatically resolve conversations after 24 hours of
                        inactivity
                      </p>
                    </div>
                    <UiSwitch
                      checked={accountData.autoResolve}
                      onCheckedChange={(checked) =>
                        setAccountData({
                          ...accountData,
                          autoResolve: checked,
                        })
                      }
                      className="shrink-0"
                    />
                  </div>
                  <Separator />
                  <div className="flex items-start justify-between gap-4 py-3">
                    <div className="min-w-0 space-y-1 pr-2">
                      <p className="text-sm font-medium leading-none">
                        Email notifications
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Send email notifications for new conversations and
                        mentions
                      </p>
                    </div>
                    <UiSwitch
                      checked={accountData.emailNotifications}
                      onCheckedChange={(checked) =>
                        setAccountData({
                          ...accountData,
                          emailNotifications: checked,
                        })
                      }
                      className="shrink-0"
                    />
                  </div>
                  {(user?.role === "administrator" ||
                    user?.role === "super_admin") && (
                    <div className="pt-4">
                      <UiButton
                        className="w-full"
                        onClick={handleSaveSettings}
                        disabled={saving}
                      >
                        {saving ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save sx={{ fontSize: "1.125rem" }} />
                            Save Changes
                          </>
                        )}
                      </UiButton>
                    </div>
                  )}
                </UiCardContent>
              </UiCard>
            </div>
          </TabPanel>

          {/* API Tokens Tab (now index 2) */}
          <TabPanel value={selectedTab} index={2}>
            <div className="flex flex-col gap-6">
              {showTokenValue && currentToken && (
                <UiAlert
                  variant="warning"
                  className="rounded-2xl border-amber-500/50 shadow-sm"
                >
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <AlertTitle className="text-base">
                        Your new API token (save it now)
                      </AlertTitle>
                      <UiButton
                        variant="outline"
                        size="sm"
                        className="shrink-0"
                        onClick={() => setShowTokenValue(false)}
                      >
                        Hide
                      </UiButton>
                    </div>
                    <AlertDescription className="mt-0 space-y-3 text-foreground">
                      <div className="space-y-3">
                        <code className="block max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-dashed border-amber-800/30 bg-background/70 p-3 font-mono text-xs leading-relaxed sm:text-sm">
                          {currentToken}
                        </code>
                        <div className="flex flex-wrap gap-2">
                          <UiButton
                            variant="secondary"
                            size="sm"
                            onClick={() => copyToClipboard(currentToken)}
                          >
                            <ContentCopy sx={{ fontSize: "1rem" }} />
                            Copy Token
                          </UiButton>
                          <UiButton
                            variant="secondary"
                            size="sm"
                            onClick={() =>
                              copyToClipboard(
                                `CONVERSE_API_TOKEN=${currentToken}`,
                              )
                            }
                          >
                            Copy as .env
                          </UiButton>
                          <UiButton
                            variant="secondary"
                            size="sm"
                            onClick={() => {
                              const accountId = apiTokens[0]?.account_id || 1;
                              const envConfig = `CONVERSE_BASE_URL=http://localhost:8001\nCONVERSE_API_TOKEN=${currentToken}\nCONVERSE_ACCOUNT_ID=${accountId}`;
                              copyToClipboard(envConfig);
                            }}
                          >
                            Copy Full Config
                          </UiButton>
                        </div>
                      </div>
                    </AlertDescription>
                  </div>
                </UiAlert>
              )}

              <div className="grid gap-6 md:grid-cols-2">
                <UiCard className="rounded-2xl border-border/60 shadow-sm">
                  <UiCardHeader className="flex flex-row items-start gap-3 space-y-0 pb-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Add sx={{ fontSize: "1.25rem" }} />
                    </div>
                    <div className="min-w-0 space-y-1.5">
                      <CardTitle className="text-base">
                        Generate New API Token
                      </CardTitle>
                      <CardDescription>
                        Create API tokens for integrating with external
                        applications like the MCP escalation client.
                      </CardDescription>
                    </div>
                  </UiCardHeader>
                  <UiCardContent className="space-y-4">
                    {!showCreateToken ? (
                      <UiButton onClick={() => setShowCreateToken(true)}>
                        <Add sx={{ fontSize: "1.125rem" }} />
                        Generate New Token
                      </UiButton>
                    ) : (
                      <div className="flex flex-col gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="settings-token-name">Token Name</Label>
                          <Input
                            id="settings-token-name"
                            value={tokenName}
                            onChange={(e) => setTokenName(e.target.value)}
                            placeholder="e.g., MCP Integration, Production Client"
                          />
                          <p className="text-xs text-muted-foreground">
                            Give your token a descriptive name
                          </p>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="settings-token-expiry">
                            Expires in (days)
                          </Label>
                          <Input
                            id="settings-token-expiry"
                            type="number"
                            min={1}
                            max={365}
                            value={tokenExpiry}
                            onChange={(e) => setTokenExpiry(e.target.value)}
                          />
                          <p className="text-xs text-muted-foreground">
                            Token will expire after this many days
                          </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          <UiButton
                            onClick={generateApiToken}
                            disabled={tokenLoading}
                          >
                            {tokenLoading ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Key sx={{ fontSize: "1.125rem" }} />
                            )}
                            {tokenLoading ? "Generating..." : "Generate Token"}
                          </UiButton>
                          <UiButton
                            variant="outline"
                            onClick={() => {
                              setShowCreateToken(false);
                              setTokenName("");
                              setTokenExpiry("30");
                            }}
                          >
                            Cancel
                          </UiButton>
                        </div>
                      </div>
                    )}
                  </UiCardContent>
                </UiCard>

                <UiCard className="rounded-2xl border-border/60 shadow-sm">
                  <UiCardHeader className="flex flex-row items-start gap-3 space-y-0 pb-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Help sx={{ fontSize: "1.25rem" }} />
                    </div>
                    <div className="min-w-0 space-y-1.5">
                      <CardTitle className="text-base">Quick Setup Guide</CardTitle>
                      <CardDescription>
                        Use your API token to configure the MCP escalation client.
                      </CardDescription>
                    </div>
                  </UiCardHeader>
                  <UiCardContent className="space-y-0">
                    <ol className="list-none space-y-4">
                      <li className="flex gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                          1
                        </span>
                        <div>
                          <p className="text-sm font-medium">Generate API Token</p>
                          <p className="text-sm text-muted-foreground">
                            Click &quot;Generate New Token&quot; and copy the value
                          </p>
                        </div>
                      </li>
                      <Separator />
                      <li className="flex gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                          2
                        </span>
                        <div>
                          <p className="text-sm font-medium">Add to .env file</p>
                          <p className="font-mono text-sm text-muted-foreground">
                            CONVERSE_API_TOKEN=your_token_here
                          </p>
                        </div>
                      </li>
                      <Separator />
                      <li className="flex gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                          3
                        </span>
                        <div>
                          <p className="text-sm font-medium">Set Account ID</p>
                          <p className="font-mono text-sm text-muted-foreground">
                            CONVERSE_ACCOUNT_ID=
                            {apiTokens[0]?.account_id || 1}
                          </p>
                        </div>
                      </li>
                      <Separator />
                      <li className="flex gap-3">
                        <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold text-muted-foreground">
                          4
                        </span>
                        <div>
                          <p className="text-sm font-medium">Test Connection</p>
                          <p className="break-all font-mono text-xs text-muted-foreground sm:text-sm">
                            curl -H &apos;Authorization: Bearer TOKEN&apos;
                            /api/v1/contacts
                          </p>
                        </div>
                      </li>
                    </ol>
                  </UiCardContent>
                </UiCard>
              </div>

              <UiCard className="rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0 pb-4">
                  <div className="flex min-w-0 flex-1 flex-row items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Key sx={{ fontSize: "1.25rem" }} />
                    </div>
                    <div className="min-w-0 space-y-1.5">
                      <CardTitle className="text-base">Active API Tokens</CardTitle>
                      <CardDescription>
                        Tokens with access to your workspace API.
                      </CardDescription>
                    </div>
                  </div>
                  <UiButton
                    variant="outline"
                    size="sm"
                    className="shrink-0"
                    onClick={loadApiTokens}
                    disabled={tokenLoading}
                  >
                    {tokenLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <Refresh sx={{ fontSize: "1.125rem" }} />
                    )}
                    Refresh
                  </UiButton>
                </UiCardHeader>
                <UiCardContent>
                  {tokenLoading ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : apiTokens.length === 0 ? (
                    <UiAlert className="rounded-xl border-border/80 bg-muted/40">
                      <AlertDescription>
                        No API tokens found. Generate your first token to get
                        started with integrations.
                      </AlertDescription>
                    </UiAlert>
                  ) : (
                    <UiTable>
                      <UiTableHeader>
                        <UiTableRow>
                          <UiTableHead>Name</UiTableHead>
                          <UiTableHead>Created</UiTableHead>
                          <UiTableHead>Last Used</UiTableHead>
                          <UiTableHead>Expires</UiTableHead>
                          <UiTableHead>Status</UiTableHead>
                          <UiTableHead className="w-[72px] text-right">
                            Actions
                          </UiTableHead>
                        </UiTableRow>
                      </UiTableHeader>
                      <UiTableBody>
                        {apiTokens.map((token) => (
                          <UiTableRow key={token.id}>
                            <UiTableCell>
                              <div className="flex items-center gap-2">
                                <Key sx={{ fontSize: "1rem", opacity: 0.65 }} />
                                <span className="font-medium">{token.name}</span>
                              </div>
                            </UiTableCell>
                            <UiTableCell className="text-muted-foreground">
                              {formatDate(token.created_at)}
                            </UiTableCell>
                            <UiTableCell className="text-muted-foreground">
                              {token.last_used_at
                                ? formatDate(token.last_used_at)
                                : "Never"}
                            </UiTableCell>
                            <UiTableCell className="text-muted-foreground">
                              {token.expires_at
                                ? formatDate(token.expires_at)
                                : "Never"}
                            </UiTableCell>
                            <UiTableCell>
                              <Badge
                                variant={
                                  token.status === "active"
                                    ? "success"
                                    : "secondary"
                                }
                              >
                                {token.status}
                              </Badge>
                            </UiTableCell>
                            <UiTableCell className="text-right">
                              <UiTooltip>
                                <TooltipTrigger asChild>
                                  <UiButton
                                    variant="ghost"
                                    size="icon"
                                    className="h-9 w-9 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                    onClick={() => revokeApiToken(token.id)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                    <span className="sr-only">Revoke token</span>
                                  </UiButton>
                                </TooltipTrigger>
                                <TooltipContent>Revoke Token</TooltipContent>
                              </UiTooltip>
                            </UiTableCell>
                          </UiTableRow>
                        ))}
                      </UiTableBody>
                    </UiTable>
                  )}
                </UiCardContent>
              </UiCard>

              <UiCard className="rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="flex flex-row items-start gap-3 space-y-0 pb-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                    <Info sx={{ fontSize: "1.25rem" }} />
                  </div>
                  <div className="min-w-0 space-y-1.5">
                    <CardTitle className="text-base">
                      Environment Configuration
                    </CardTitle>
                    <CardDescription>
                      Copy this configuration to your MCP client&apos;s .env file.
                    </CardDescription>
                  </div>
                </UiCardHeader>
                <UiCardContent className="space-y-4">
                  <pre className="overflow-x-auto rounded-lg border border-border/80 bg-muted/50 p-4 font-mono text-xs leading-relaxed sm:text-sm">
                    {`# Converse Configuration
CONVERSE_BASE_URL=http://localhost:8001
CONVERSE_API_TOKEN=<your_generated_token>
CONVERSE_ACCOUNT_ID=${apiTokens[0]?.account_id || 1}

# Database Configuration
MYSQL_HOST=localhost
MYSQL_PORT=3306
MYSQL_USER=root
MYSQL_PASSWORD=your_password
MYSQL_DB=converse_mcp`}
                  </pre>
                  <UiButton
                    variant="secondary"
                    onClick={() =>
                      copyToClipboard(`# Converse Configuration
CONVERSE_BASE_URL=http://localhost:8001
CONVERSE_API_TOKEN=<your_generated_token>
CONVERSE_ACCOUNT_ID=${apiTokens[0]?.account_id || 1}

# Database Configuration
MYSQL_HOST=localhost
MYSQL_PORT=3306
MYSQL_USER=root
MYSQL_PASSWORD=your_password
MYSQL_DB=converse_mcp`)
                    }
                  >
                    <ContentCopy sx={{ fontSize: "1rem" }} />
                    Copy Configuration
                  </UiButton>
                </UiCardContent>
              </UiCard>
            </div>
          </TabPanel>

          {/* Audit Logs (now index 3) */}
          <TabPanel value={selectedTab} index={3}>
            <div className="flex flex-col gap-4">
              <UiCard className="overflow-hidden rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="border-b border-border/60 bg-muted/25 pb-5 pt-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex flex-row items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <History sx={{ fontSize: "1.35rem" }} />
                      </div>
                      <div className="min-w-0 space-y-1.5">
                        <CardTitle className="text-xl font-semibold tracking-tight">
                          Activity &amp; audit logs
                        </CardTitle>
                        <CardDescription className="max-w-2xl text-sm leading-relaxed">
                          Review events, filter the list, and open a row for full
                          context.
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="shrink-0 font-normal">
                      {filteredAuditLogs.length === auditLogs.length
                        ? `${auditLogs.length} events`
                        : `${filteredAuditLogs.length} of ${auditLogs.length} shown`}
                    </Badge>
                  </div>
                </UiCardHeader>

                <UiCardContent className="p-0">
                  <div className="bg-muted/20 px-4 py-4 md:px-6">
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
                      <div className="grid w-full flex-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        <div className="space-y-2">
                          <Label htmlFor="audit-search">Search</Label>
                          <div className="relative">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                            <Input
                              id="audit-search"
                              className="pl-9"
                              placeholder="User, action, IP, or details…"
                              value={auditSearchQuery}
                              onChange={(e) =>
                                setAuditSearchQuery(e.target.value)
                              }
                            />
                          </div>
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="audit-date">Date</Label>
                          <Input
                            id="audit-date"
                            type="date"
                            value={auditDateFilter}
                            onChange={(e) =>
                              setAuditDateFilter(e.target.value)
                            }
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="audit-action-type">Action type</Label>
                          <Select
                            value={auditActionFilter}
                            onValueChange={setAuditActionFilter}
                          >
                            <SelectTrigger id="audit-action-type">
                              <SelectValue placeholder="Action type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All actions</SelectItem>
                              <SelectItem value="login">Logins</SelectItem>
                              <SelectItem value="api">API calls</SelectItem>
                              <SelectItem value="settings">Settings</SelectItem>
                              <SelectItem value="tokens">API tokens</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      <div className="flex w-full shrink-0 flex-col gap-2 sm:flex-row sm:items-center lg:w-auto">
                        {(auditSearchQuery.trim() !== "" ||
                          auditDateFilter !== "" ||
                          auditActionFilter !== "all") && (
                          <UiButton
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-muted-foreground"
                            onClick={() => {
                              setAuditSearchQuery("");
                              setAuditDateFilter("");
                              setAuditActionFilter("all");
                            }}
                          >
                            Clear filters
                          </UiButton>
                        )}
                        <UiButton
                          variant="outline"
                          className="w-full sm:w-auto"
                          onClick={() => loadAuditLogs()}
                        >
                          <Refresh sx={{ fontSize: "1.125rem" }} />
                          Refresh
                        </UiButton>
                      </div>
                    </div>
                  </div>
                </UiCardContent>
              </UiCard>

              <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
                <UiTable>
                  <UiTableHeader>
                    <UiTableRow className="hover:bg-transparent">
                      <UiTableHead className="w-[200px]">Timestamp</UiTableHead>
                      <UiTableHead>User</UiTableHead>
                      <UiTableHead>Action</UiTableHead>
                      <UiTableHead className="hidden md:table-cell">
                        IP address
                      </UiTableHead>
                      <UiTableHead>Status</UiTableHead>
                      <UiTableHead className="text-right">Details</UiTableHead>
                    </UiTableRow>
                  </UiTableHeader>
                  <UiTableBody>
                    {filteredAuditLogs.length === 0 ? (
                      <UiTableRow>
                        <UiTableCell
                          colSpan={6}
                          className="h-24 text-center text-muted-foreground"
                        >
                          No events match your filters.
                        </UiTableCell>
                      </UiTableRow>
                    ) : (
                      filteredAuditLogs.map((log) => (
                        <UiTableRow key={log.id}>
                          <UiTableCell className="align-top text-muted-foreground">
                            <div className="flex items-center gap-2 text-foreground">
                              <AccessTime
                                sx={{ fontSize: "1rem", opacity: 0.7 }}
                              />
                              <span className="whitespace-nowrap text-xs sm:text-sm">
                                {new Date(log.timestamp).toLocaleString()}
                              </span>
                            </div>
                          </UiTableCell>
                          <UiTableCell className="align-top">
                            <div className="flex items-center gap-2">
                              <ScnAvatar className="h-8 w-8">
                                <ScnAvatarFallback className="text-xs font-medium">
                                  {log.user.charAt(0)}
                                </ScnAvatarFallback>
                              </ScnAvatar>
                              <span className="text-sm font-medium">
                                {log.user}
                              </span>
                            </div>
                          </UiTableCell>
                          <UiTableCell className="align-top">
                            <Badge
                              variant={
                                log.action.includes("Login")
                                  ? "default"
                                  : log.action.includes("API")
                                    ? "secondary"
                                    : log.action.includes("Settings")
                                      ? "outline"
                                      : "secondary"
                              }
                              className="font-normal"
                            >
                              {log.action}
                            </Badge>
                          </UiTableCell>
                          <UiTableCell className="hidden align-top font-mono text-xs text-muted-foreground md:table-cell">
                            {log.ip}
                          </UiTableCell>
                          <UiTableCell className="align-top">
                            <Badge
                              variant={
                                log.status === "success"
                                  ? "success"
                                  : "destructive"
                              }
                            >
                              {log.status}
                            </Badge>
                          </UiTableCell>
                          <UiTableCell className="align-top text-right">
                            <UiButton
                              variant="ghost"
                              size="sm"
                              className="text-primary"
                              onClick={() => {
                                setSelectedAuditLog(log);
                                setAuditLogDetailOpen(true);
                              }}
                            >
                              View
                            </UiButton>
                          </UiTableCell>
                        </UiTableRow>
                      ))
                    )}
                  </UiTableBody>
                </UiTable>
              </div>

              <div className="grid overflow-hidden rounded-2xl border border-border/60 bg-muted/10 md:grid-cols-12">
                  <div className="border-b border-border/60 p-5 md:col-span-4 md:border-b-0 md:border-r">
                    <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Security snapshot
                    </p>
                    <div className="space-y-4">
                      <div className="flex gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        <div>
                          <p className="text-sm font-medium leading-tight">
                            Two-factor authentication
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Enabled
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600 dark:text-emerald-400" />
                        <div>
                          <p className="text-sm font-medium leading-tight">
                            API access logging
                          </p>
                          <p className="text-xs text-muted-foreground">Active</p>
                        </div>
                      </div>
                      <div className="flex gap-3">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
                        <div>
                          <p className="text-sm font-medium leading-tight">
                            Password policy
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Review recommended
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="p-5 md:col-span-8">
                    <div className="mb-4 flex items-center gap-2">
                      <Info
                        sx={{ fontSize: "1.125rem", color: "inherit" }}
                        className="text-muted-foreground"
                      />
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Activity summary (filtered list)
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-6 sm:flex-nowrap sm:justify-between sm:gap-8">
                      <div className="min-w-[4.5rem] flex-1 text-center">
                        <p className="text-2xl font-semibold tabular-nums text-foreground sm:text-3xl">
                          {
                            filteredAuditLogs.filter((log) =>
                              log.action.includes("Login"),
                            ).length
                          }
                        </p>
                        <p className="mt-1 text-xs font-medium text-muted-foreground">
                          Logins
                        </p>
                      </div>
                      <div className="min-w-[4.5rem] flex-1 text-center">
                        <p className="text-2xl font-semibold tabular-nums text-foreground sm:text-3xl">
                          {
                            filteredAuditLogs.filter((log) =>
                              log.action.includes("API"),
                            ).length
                          }
                        </p>
                        <p className="mt-1 text-xs font-medium text-muted-foreground">
                          API calls
                        </p>
                      </div>
                      <div className="min-w-[4.5rem] flex-1 text-center">
                        <p className="text-2xl font-semibold tabular-nums text-foreground sm:text-3xl">
                          {
                            filteredAuditLogs.filter((log) =>
                              log.action.includes("Settings"),
                            ).length
                          }
                        </p>
                        <p className="mt-1 text-xs font-medium text-muted-foreground">
                          Config changes
                        </p>
                      </div>
                      <div className="min-w-[4.5rem] flex-1 text-center">
                        <p className="text-2xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400 sm:text-3xl">
                          {
                            filteredAuditLogs.filter(
                              (log) => log.status === "success",
                            ).length
                          }
                        </p>
                        <p className="mt-1 text-xs font-medium text-muted-foreground">
                          Successful
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
            </div>

            <UiDialog
              open={auditLogDetailOpen}
              onOpenChange={(open) => {
                setAuditLogDetailOpen(open);
                if (!open) setSelectedAuditLog(null);
              }}
            >
              <UiDialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
                <UiDialogHeader>
                  <UiDialogTitle>
                    {selectedAuditLog?.action || "Log details"}
                  </UiDialogTitle>
                  <UiDialogDescription>
                    {selectedAuditLog
                      ? new Date(selectedAuditLog.timestamp).toLocaleString()
                      : ""}
                  </UiDialogDescription>
                </UiDialogHeader>
                {selectedAuditLog && (
                  <div className="grid gap-3 text-sm">
                    <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
                      <span className="text-muted-foreground">User</span>
                      <p className="font-medium">{selectedAuditLog.user}</p>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
                      <span className="text-muted-foreground">IP address</span>
                      <p className="font-mono text-xs sm:text-sm">
                        {selectedAuditLog.ip}
                      </p>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
                      <span className="text-muted-foreground">Status</span>
                      <div className="mt-1">
                        <Badge
                          variant={
                            selectedAuditLog.status === "success"
                              ? "success"
                              : "destructive"
                          }
                        >
                          {selectedAuditLog.status}
                        </Badge>
                      </div>
                    </div>
                    <div className="rounded-lg border border-border/60 bg-muted/30 px-3 py-2">
                      <span className="text-muted-foreground">Details</span>
                      <p className="mt-1 leading-relaxed">
                        {selectedAuditLog.details}
                      </p>
                    </div>
                  </div>
                )}
                <UiDialogFooter>
                  <UiButton
                    variant="outline"
                    onClick={() => setAuditLogDetailOpen(false)}
                  >
                    Close
                  </UiButton>
                </UiDialogFooter>
              </UiDialogContent>
            </UiDialog>
          </TabPanel>

          {/* SLA & Performance (now index 4) */}
          <TabPanel value={selectedTab} index={4}>
            <div className="flex flex-col gap-4">
              <UiCard className="overflow-hidden rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="border-b border-border/60 bg-muted/25 pb-5 pt-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex flex-row items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Speed sx={{ fontSize: "1.35rem" }} />
                      </div>
                      <div className="min-w-0 space-y-1.5">
                        <CardTitle className="text-xl font-semibold tracking-tight">
                          SLA &amp; performance
                        </CardTitle>
                        <CardDescription className="max-w-2xl text-sm leading-relaxed">
                          Monitor SLA targets, trends, and workspace defaults for
                          the selected period.
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="shrink-0 font-normal">
                      {performanceDateRange === "7days"
                        ? "Last 7 days"
                        : performanceDateRange === "30days"
                          ? "Last 30 days"
                          : "Custom range"}
                    </Badge>
                  </div>
                </UiCardHeader>

                <UiCardContent className="space-y-0 p-0">
                  <div className="border-b border-border/60 bg-muted/20 px-4 py-4 md:px-6">
                    <Label className="mb-3 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      Report period
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      <UiButton
                        type="button"
                        variant={
                          performanceDateRange === "7days"
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        onClick={() => handleDateRangeChange("7days")}
                      >
                        Last 7 days
                      </UiButton>
                      <UiButton
                        type="button"
                        variant={
                          performanceDateRange === "30days"
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        onClick={() => handleDateRangeChange("30days")}
                      >
                        Last 30 days
                      </UiButton>
                      <UiButton
                        type="button"
                        variant={
                          performanceDateRange === "custom"
                            ? "default"
                            : "outline"
                        }
                        size="sm"
                        onClick={() => handleDateRangeChange("custom")}
                      >
                        Custom range
                      </UiButton>
                    </div>
                  </div>

                  <div className="px-4 py-5 md:px-6">
                    <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                      SLA metrics
                    </p>
                    <div className="overflow-hidden rounded-xl border border-border/50 bg-border/30">
                      <div className="grid gap-px sm:grid-cols-2 lg:grid-cols-4">
                        {slaData.map((metric, index) => (
                          <div
                            key={index}
                            className="bg-card p-4 text-center sm:text-left"
                          >
                            <p className="text-sm font-medium leading-snug">
                              {metric.metric}
                            </p>
                            <p
                              className={
                                metric.status === "excellent"
                                  ? "mt-2 text-2xl font-semibold tabular-nums text-emerald-600 dark:text-emerald-400"
                                  : metric.status === "good"
                                    ? "mt-2 text-2xl font-semibold tabular-nums text-primary"
                                    : "mt-2 text-2xl font-semibold tabular-nums text-amber-600 dark:text-amber-400"
                              }
                            >
                              {metric.current}
                            </p>
                            <p className="mt-1 text-xs text-muted-foreground">
                              Target: {metric.target}
                            </p>
                            <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                              <div
                                className={
                                  metric.status === "excellent"
                                    ? "h-full rounded-full bg-emerald-500 transition-all"
                                    : metric.status === "good"
                                      ? "h-full rounded-full bg-primary transition-all"
                                      : "h-full rounded-full bg-amber-500 transition-all"
                                }
                                style={{
                                  width: `${Math.min(100, Math.max(0, metric.percentage))}%`,
                                }}
                              />
                            </div>
                            <p className="mt-2 text-xs text-muted-foreground">
                              {metric.percentage}% of target
                            </p>
                            <div className="mt-2 flex justify-center sm:justify-start">
                              <Badge
                                variant={
                                  metric.status === "excellent"
                                    ? "success"
                                    : metric.status === "good"
                                      ? "default"
                                      : "warning"
                                }
                                className="font-normal"
                              >
                                {metric.status === "excellent"
                                  ? "Excellent"
                                  : metric.status === "good"
                                    ? "On track"
                                    : "Needs attention"}
                              </Badge>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="grid border-t border-border/60 bg-muted/5 md:grid-cols-12">
                    <div className="border-b border-border/60 p-5 md:col-span-7 md:border-b-0 md:border-r lg:col-span-8">
                      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Performance trend
                      </p>
                      <div className="flex h-[280px] flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/60 bg-muted/20 px-4 text-center">
                        <p className="text-sm font-medium text-muted-foreground">
                          Chart placeholder
                        </p>
                        <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
                          Hook up Chart.js, Recharts, or another library here for
                          response and resolution trends over time.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-5 px-6 py-7 md:px-8 md:py-8 md:col-span-5 lg:col-span-4">
                      <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Business sx={{ fontSize: "1.125rem" }} />
                        </div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          SLA configuration
                        </p>
                      </div>
                      <div className="divide-y divide-border/60 rounded-lg border border-border/50 px-4 py-2 sm:px-5 sm:py-3">
                        <div className="flex items-start justify-between gap-3 px-1 py-4 sm:px-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">
                              First response SLA
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {slaConfig.firstResponseTime} minutes
                            </p>
                          </div>
                          <UiButton
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() =>
                              handleSLAConfigEdit("firstResponseTime")
                            }
                          >
                            <Edit sx={{ fontSize: "1.125rem" }} />
                          </UiButton>
                        </div>
                        <div className="flex items-start justify-between gap-3 px-1 py-4 sm:px-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">
                              Resolution time SLA
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {slaConfig.resolutionTime} hours
                            </p>
                          </div>
                          <UiButton
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() =>
                              handleSLAConfigEdit("resolutionTime")
                            }
                          >
                            <Edit sx={{ fontSize: "1.125rem" }} />
                          </UiButton>
                        </div>
                        <div className="flex items-start justify-between gap-3 px-1 py-4 sm:px-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">
                              Customer satisfaction target
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {slaConfig.csatTarget}%
                            </p>
                          </div>
                          <UiButton
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() => handleSLAConfigEdit("csatTarget")}
                          >
                            <Edit sx={{ fontSize: "1.125rem" }} />
                          </UiButton>
                        </div>
                        <div className="flex items-start justify-between gap-3 px-1 py-4 sm:px-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">Working hours</p>
                            <p className="text-xs text-muted-foreground">
                              {slaConfig.workingHours}
                            </p>
                          </div>
                          <UiButton
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0"
                            onClick={() =>
                              handleSLAConfigEdit("workingHours")
                            }
                          >
                            <Edit sx={{ fontSize: "1.125rem" }} />
                          </UiButton>
                        </div>
                      </div>
                      <UiButton
                        type="button"
                        className="w-full gap-2"
                        onClick={handleSaveSLAConfig}
                        disabled={saving}
                      >
                        {saving ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Save sx={{ fontSize: "1.125rem" }} />
                        )}
                        Update SLA settings
                      </UiButton>
                    </div>
                  </div>
                </UiCardContent>
              </UiCard>

              <div className="overflow-hidden rounded-2xl border border-border/60 bg-card shadow-sm">
                <div className="flex flex-col gap-3 border-b border-border/60 bg-muted/20 px-4 py-4 sm:flex-row sm:items-center sm:justify-between md:px-6">
                  <div>
                    <h3 className="text-sm font-semibold tracking-tight">
                      Team performance
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      Breakdown by agent for the selected period.
                    </p>
                  </div>
                  <UiButton variant="outline" size="sm" className="gap-2 shrink-0">
                    <Visibility sx={{ fontSize: "1.125rem" }} />
                    View detailed report
                  </UiButton>
                </div>
                <UiTable>
                  <UiTableHeader>
                    <UiTableRow className="hover:bg-transparent">
                      <UiTableHead>Agent</UiTableHead>
                      <UiTableHead>Conversations</UiTableHead>
                      <UiTableHead className="hidden sm:table-cell">
                        Avg response time
                      </UiTableHead>
                      <UiTableHead className="hidden md:table-cell">
                        Avg resolution time
                      </UiTableHead>
                      <UiTableHead>Customer rating</UiTableHead>
                      <UiTableHead>Status</UiTableHead>
                    </UiTableRow>
                  </UiTableHeader>
                  <UiTableBody>
                    {teamPerformanceData.length > 0 ? (
                      teamPerformanceData.map((agent, index) => (
                        <UiTableRow key={index}>
                          <UiTableCell className="align-middle">
                            <div className="flex items-center gap-2">
                              <ScnAvatar className="h-8 w-8">
                                <ScnAvatarFallback className="text-xs font-medium">
                                  {agent.name
                                    .split(" ")
                                    .map((n: string) => n[0])
                                    .join("")}
                                </ScnAvatarFallback>
                              </ScnAvatar>
                              <span className="text-sm font-medium">
                                {agent.name}
                              </span>
                            </div>
                          </UiTableCell>
                          <UiTableCell className="align-middle tabular-nums">
                            {agent.conversations}
                          </UiTableCell>
                          <UiTableCell className="hidden align-middle sm:table-cell">
                            {agent.responseTime}
                          </UiTableCell>
                          <UiTableCell className="hidden align-middle md:table-cell">
                            {agent.resolutionTime}
                          </UiTableCell>
                          <UiTableCell className="align-middle">
                            <span className="text-sm tabular-nums">
                              ★ {agent.rating}
                            </span>
                          </UiTableCell>
                          <UiTableCell className="align-middle">
                            <Badge
                              variant={
                                agent.status === "excellent"
                                  ? "success"
                                  : agent.status === "good"
                                    ? "default"
                                    : "warning"
                              }
                              className="font-normal"
                            >
                              {agent.status === "excellent"
                                ? "Excellent"
                                : agent.status === "good"
                                  ? "Good"
                                  : "Needs improvement"}
                            </Badge>
                          </UiTableCell>
                        </UiTableRow>
                      ))
                    ) : (
                      <UiTableRow>
                        <UiTableCell
                          colSpan={6}
                          className="h-24 text-center text-muted-foreground"
                        >
                          {loading ? (
                            <Loader2 className="mx-auto h-6 w-6 animate-spin" />
                          ) : (
                            "No team performance data for the selected date range."
                          )}
                        </UiTableCell>
                      </UiTableRow>
                    )}
                  </UiTableBody>
                </UiTable>
              </div>
            </div>
          </TabPanel>

          {/* Help & Support (now index 5) */}
          <TabPanel value={selectedTab} index={5}>
            <div className="flex flex-col gap-4">
              <UiCard className="overflow-hidden rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="border-b border-border/60 bg-muted/25 pb-5 pt-6">
                  <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                    <div className="flex flex-row items-start gap-3">
                      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                        <Help sx={{ fontSize: "1.35rem" }} />
                      </div>
                      <div className="min-w-0 space-y-1.5">
                        <CardTitle className="text-xl font-semibold tracking-tight">
                          Help &amp; support
                        </CardTitle>
                        <CardDescription className="max-w-2xl text-sm leading-relaxed">
                          Reach the team, copy diagnostics for tickets, and open
                          self-serve resources.
                        </CardDescription>
                      </div>
                    </div>
                    <Badge variant="secondary" className="shrink-0 font-normal">
                      {systemInfo.status === "operational"
                        ? "Systems operational"
                        : "Checking status…"}
                    </Badge>
                  </div>
                </UiCardHeader>
                <UiCardContent className="space-y-0 p-0">
                  <div className="grid md:grid-cols-2">
                    <div className="border-b border-border/60 p-6 md:border-b-0 md:border-r md:p-8">
                      <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Get in touch
                      </p>
                      <div className="flex flex-col gap-3">
                        <UiButton
                          type="button"
                          className="w-full gap-2"
                          onClick={() => setContactSupportOpen(true)}
                        >
                          <Email sx={{ fontSize: "1.125rem" }} />
                          Submit support ticket
                        </UiButton>
                        <UiButton
                          type="button"
                          variant="outline"
                          className="w-full gap-2"
                          onClick={() => setScheduleDemoOpen(true)}
                        >
                          <Business sx={{ fontSize: "1.125rem" }} />
                          Schedule demo call
                        </UiButton>
                      </div>
                      <Separator className="my-6" />
                      <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        Quick contact
                      </p>
                      <div className="divide-y divide-border/60 rounded-lg border border-border/50">
                        <div className="flex items-start gap-3 px-4 py-3.5 sm:px-5">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                            <Email sx={{ fontSize: "1.125rem" }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">Email support</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {process.env.REACT_APP_CONTACT_EMAIL ||
                                "contact@mail.rentremote.com"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 px-4 py-3.5 sm:px-5">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                            <Phone sx={{ fontSize: "1.125rem" }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">Phone support</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {process.env.REACT_APP_CONTACT_PHONE ||
                                "+1 (365) 517-0433"}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3 px-4 py-3.5 sm:px-5">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-muted text-primary">
                            <Web sx={{ fontSize: "1.125rem" }} />
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium">Knowledge base</p>
                            <UiButton
                              type="button"
                              variant="link"
                              className="h-auto p-0 text-xs font-normal text-primary"
                              onClick={() => navigate("/dashboard/help")}
                            >
                              Open /help
                            </UiButton>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="p-6 md:p-8">
                      <div className="mb-1 flex items-center gap-2">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Info sx={{ fontSize: "1.125rem" }} />
                        </div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          System information
                        </p>
                      </div>
                      <p className="mb-4 text-sm text-muted-foreground">
                        Share with support when you open a ticket.
                      </p>
                      <div className="divide-y divide-border/60 rounded-lg border border-border/50">
                        <div className="flex items-center justify-between gap-3 px-4 py-3.5 sm:px-5">
                          <div className="min-w-0">
                            <p className="text-sm font-medium">Version</p>
                            <p className="truncate text-xs text-muted-foreground">
                              {systemInfo.version || "Loading…"}
                            </p>
                          </div>
                          <Badge
                            variant={
                              systemInfo.status === "operational"
                                ? "success"
                                : "secondary"
                            }
                            className="shrink-0 font-normal"
                          >
                            {systemInfo.status === "operational"
                              ? "Operational"
                              : "Loading"}
                          </Badge>
                        </div>
                        <div className="px-4 py-3.5 sm:px-5">
                          <p className="text-sm font-medium">Instance ID</p>
                          <p className="mt-0.5 truncate font-mono text-xs text-muted-foreground">
                            {systemInfo.instanceId || "Loading…"}
                          </p>
                        </div>
                        <div className="px-4 py-3.5 sm:px-5">
                          <p className="text-sm font-medium">Region</p>
                          <p className="mt-0.5 text-xs text-muted-foreground">
                            {systemInfo.region || "Loading…"}
                          </p>
                        </div>
                        <div className="px-4 py-3.5 sm:px-5">
                          <p className="text-sm font-medium">Account ID</p>
                          <p className="mt-0.5 font-mono text-xs text-muted-foreground">
                            {apiTokens[0]?.account_id ?? "Not available"}
                          </p>
                        </div>
                      </div>
                      <UiButton
                        type="button"
                        variant="outline"
                        className="mt-5 w-full gap-2"
                        onClick={() =>
                          copyToClipboard(
                            `Version: ${systemInfo.version}\nInstance: ${systemInfo.instanceId}\nRegion: ${systemInfo.region}\nAccount: ${apiTokens[0]?.account_id || "N/A"}`,
                          )
                        }
                      >
                        <ContentCopy sx={{ fontSize: "1.125rem" }} />
                        Copy system info
                      </UiButton>
                    </div>
                  </div>
                </UiCardContent>
              </UiCard>

              <UiCard className="overflow-hidden rounded-2xl border-border/60 shadow-sm">
                <UiCardHeader className="border-b border-border/60 bg-muted/25 pb-4 pt-5 sm:pb-5 sm:pt-6">
                  <div className="flex flex-row items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                      <Description sx={{ fontSize: "1.25rem" }} />
                    </div>
                    <div className="min-w-0 space-y-1">
                      <CardTitle className="text-lg font-semibold tracking-tight">
                        Documentation &amp; resources
                      </CardTitle>
                      <CardDescription className="text-sm leading-relaxed">
                        API references, guides, and tutorials in one strip.
                      </CardDescription>
                    </div>
                  </div>
                </UiCardHeader>
                <UiCardContent className="p-4 pt-2 md:p-6 md:pt-4">
                  <div className="overflow-hidden rounded-xl border border-border/50 bg-border/30">
                    <div className="grid gap-px sm:grid-cols-3">
                      <div className="flex flex-col bg-card p-5 text-left">
                        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Description sx={{ fontSize: "1.25rem" }} />
                        </div>
                        <p className="text-sm font-semibold">API documentation</p>
                        <p className="mt-1 flex-1 text-xs leading-relaxed text-muted-foreground">
                          Complete API reference and integration guides.
                        </p>
                        <UiButton
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-4 w-full sm:mt-auto"
                          onClick={() => navigate("/dashboard/api-docs")}
                        >
                          View API docs
                        </UiButton>
                      </div>
                      <div className="flex flex-col bg-card p-5 text-left">
                        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Help sx={{ fontSize: "1.25rem" }} />
                        </div>
                        <p className="text-sm font-semibold">User guides</p>
                        <p className="mt-1 flex-1 text-xs leading-relaxed text-muted-foreground">
                          Step-by-step tutorials and best practices.
                        </p>
                        <UiButton
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-4 w-full sm:mt-auto"
                          onClick={() => navigate("/dashboard/help")}
                        >
                          Browse guides
                        </UiButton>
                      </div>
                      <div className="flex flex-col bg-card p-5 text-left">
                        <div className="mb-3 flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                          <Web sx={{ fontSize: "1.25rem" }} />
                        </div>
                        <p className="text-sm font-semibold">Video tutorials</p>
                        <p className="mt-1 flex-1 text-xs leading-relaxed text-muted-foreground">
                          Learn through short walkthrough videos.
                        </p>
                        <UiButton
                          type="button"
                          variant="outline"
                          size="sm"
                          className="mt-4 w-full sm:mt-auto"
                          disabled
                        >
                          Watch videos
                        </UiButton>
                      </div>
                    </div>
                  </div>
                </UiCardContent>
              </UiCard>

              <UiCard className="overflow-hidden rounded-2xl border-border/60 shadow-sm">
                <UiCardContent className="grid gap-0 p-0 md:grid-cols-2">
                  <div className="border-b border-border/60 p-6 md:border-b-0 md:border-r md:p-8">
                    <div className="mb-5 flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <NotificationsActive sx={{ fontSize: "1.125rem" }} />
                      </div>
                      <div className="min-w-0 space-y-1">
                        <p className="text-sm font-semibold tracking-tight">
                          Community &amp; updates
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Forum, status, and release notes.
                        </p>
                      </div>
                    </div>
                    <div className="divide-y divide-border/60 rounded-lg border border-border/50">
                      <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                        <div className="flex min-w-0 items-start gap-3">
                          <Web
                            className="mt-0.5 shrink-0 text-primary"
                            sx={{ fontSize: "1.25rem" }}
                          />
                          <div>
                            <p className="text-sm font-medium">Community forum</p>
                            <p className="text-xs text-muted-foreground">
                              Connect with other Converse users
                            </p>
                          </div>
                        </div>
                        <UiButton type="button" variant="outline" size="sm">
                          Visit
                        </UiButton>
                      </div>
                      <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                        <div className="flex min-w-0 items-start gap-3">
                          <NotificationsActive
                            className="mt-0.5 shrink-0 text-primary"
                            sx={{ fontSize: "1.25rem" }}
                          />
                          <div>
                            <p className="text-sm font-medium">Status page</p>
                            <p className="text-xs text-muted-foreground">
                              System status and incidents
                            </p>
                          </div>
                        </div>
                        <UiButton type="button" variant="outline" size="sm">
                          Check
                        </UiButton>
                      </div>
                      <div className="flex flex-col gap-3 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
                        <div className="flex min-w-0 items-start gap-3">
                          <Description
                            className="mt-0.5 shrink-0 text-primary"
                            sx={{ fontSize: "1.25rem" }}
                          />
                          <div>
                            <p className="text-sm font-medium">Changelog</p>
                            <p className="text-xs text-muted-foreground">
                              Latest features and fixes
                            </p>
                          </div>
                        </div>
                        <UiButton
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => navigate("/dashboard/changelog")}
                        >
                          View
                        </UiButton>
                      </div>
                    </div>
                  </div>
                  <div className="p-6 md:p-8">
                    <div className="mb-5 flex items-start gap-3">
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <Speed sx={{ fontSize: "1.125rem" }} />
                      </div>
                      <div className="min-w-0 space-y-1">
                        <p className="text-sm font-semibold tracking-tight">
                          Quick actions
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Common tasks without leaving settings.
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1">
                      <UiButton
                        type="button"
                        variant="ghost"
                        className="h-auto w-full justify-start gap-3 px-3 py-3 text-left font-normal"
                        onClick={() => navigate("/dashboard/my-profile")}
                      >
                        <AccountCircle
                          className="shrink-0 text-muted-foreground"
                          sx={{ fontSize: "1.35rem" }}
                        />
                        <div className="min-w-0 text-left">
                          <p className="text-sm font-medium">Update profile</p>
                          <p className="text-xs text-muted-foreground">
                            Change your account information
                          </p>
                        </div>
                      </UiButton>
                      <UiButton
                        type="button"
                        variant="ghost"
                        className="h-auto w-full justify-start gap-3 px-3 py-3 text-left font-normal"
                        onClick={() =>
                          navigate("/dashboard/my-profile?tab=security")
                        }
                      >
                        <Security
                          className="shrink-0 text-muted-foreground"
                          sx={{ fontSize: "1.35rem" }}
                        />
                        <div className="min-w-0 text-left">
                          <p className="text-sm font-medium">Reset password</p>
                          <p className="text-xs text-muted-foreground">
                            Change your account password
                          </p>
                        </div>
                      </UiButton>
                      <UiButton
                        type="button"
                        variant="ghost"
                        className="h-auto w-full justify-start gap-3 px-3 py-3 text-left font-normal"
                      >
                        <Refresh
                          className="shrink-0 text-muted-foreground"
                          sx={{ fontSize: "1.35rem" }}
                        />
                        <div className="min-w-0 text-left">
                          <p className="text-sm font-medium">Clear cache</p>
                          <p className="text-xs text-muted-foreground">
                            Refresh your session data
                          </p>
                        </div>
                      </UiButton>
                      <UiButton
                        type="button"
                        variant="ghost"
                        className="h-auto w-full justify-start gap-3 px-3 py-3 text-left font-normal"
                        onClick={() => navigate("/dashboard/settings?tab=0")}
                      >
                        <Key
                          className="shrink-0 text-muted-foreground"
                          sx={{ fontSize: "1.35rem" }}
                        />
                        <div className="min-w-0 text-left">
                          <p className="text-sm font-medium">
                            Regenerate API keys
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Create new API tokens
                          </p>
                        </div>
                      </UiButton>
                    </div>
                  </div>
                </UiCardContent>
              </UiCard>
            </div>
          </TabPanel>

          {/* Co-Pilot Settings (now index 6) */}
          <TabPanel value={selectedTab} index={6}>
            <div className="rounded-xl border border-border/40 bg-muted/5 p-2 md:p-4">
              <CoPilotSettingsTab />
            </div>
          </TabPanel>

          {/* AI Configuration (now index 7) */}
          <TabPanel value={selectedTab} index={7}>
            <div className="rounded-xl border border-border/40 bg-muted/5 p-2 md:p-4">
              <AIConfigPanel />
            </div>
          </TabPanel>
            </Box>
          </UiTabs>
        </div>
      </div>

      {/* Support Ticket Dialog */}
      <Dialog
        open={contactSupportOpen}
        onClose={() => setContactSupportOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Submit Support Ticket</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please provide details about your issue and our support team will
            get back to you shortly.
          </Typography>

          <TextField
            fullWidth
            label="Your Name"
            id="ticket-name"
            sx={{ mb: 2 }}
            defaultValue={user?.full_name || ""}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            id="ticket-email"
            sx={{ mb: 2 }}
            defaultValue={user?.email || ""}
          />

          <TextField
            fullWidth
            select
            label="Category"
            id="ticket-category"
            defaultValue="general"
            sx={{ mb: 2 }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="general">General Inquiry</option>
            <option value="bug">Bug Report</option>
            <option value="feature_request">Feature Request</option>
            <option value="question">Question</option>
            <option value="technical_issue">Technical Issue</option>
            <option value="account_issue">Account Issue</option>
            <option value="other">Other</option>
          </TextField>

          <TextField
            fullWidth
            select
            label="Priority"
            id="ticket-priority"
            defaultValue="medium"
            sx={{ mb: 2 }}
            SelectProps={{
              native: true,
            }}
          >
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </TextField>

          <TextField
            fullWidth
            label="Subject"
            id="ticket-subject"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Description"
            id="ticket-description"
            multiline
            rows={6}
            placeholder="Please describe your issue in detail..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactSupportOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const nameInput = document.getElementById(
                "ticket-name",
              ) as HTMLInputElement;
              const emailInput = document.getElementById(
                "ticket-email",
              ) as HTMLInputElement;
              const categoryInput = document.getElementById(
                "ticket-category",
              ) as HTMLSelectElement;
              const priorityInput = document.getElementById(
                "ticket-priority",
              ) as HTMLSelectElement;
              const subjectInput = document.getElementById(
                "ticket-subject",
              ) as HTMLInputElement;
              const descriptionInput = document.getElementById(
                "ticket-description",
              ) as HTMLTextAreaElement;

              try {
                setSaving(true);
                const response = await api.post("/support/ticket", {
                  name: nameInput?.value,
                  email: emailInput?.value,
                  category: categoryInput?.value,
                  priority: priorityInput?.value,
                  subject: subjectInput?.value,
                  description: descriptionInput?.value,
                });

                setSnackbarMessage(
                  `Support ticket created! Ticket number: ${response.data.ticket_number}`,
                );
                setSnackbarOpen(true);
                setContactSupportOpen(false);

                // Clear form
                if (subjectInput) subjectInput.value = "";
                if (descriptionInput) descriptionInput.value = "";
              } catch (error: any) {
                setSnackbarMessage(
                  "Failed to create support ticket. Please try again.",
                );
                setSnackbarOpen(true);
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : "Submit Ticket"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Demo Dialog */}
      <Dialog
        open={scheduleDemoOpen}
        onClose={() => setScheduleDemoOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Schedule Demo Call</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Schedule a personalized demo to see how Converse can transform your
            customer support.
          </Typography>

          <TextField
            fullWidth
            label="Your Name"
            id="demo-name"
            sx={{ mb: 2, mt: 1 }}
            defaultValue={user?.full_name || ""}
          />

          <TextField
            fullWidth
            label="Email"
            type="email"
            id="demo-email"
            sx={{ mb: 2 }}
            defaultValue={user?.email || ""}
          />

          <TextField
            fullWidth
            label="Company"
            id="demo-company"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Phone (Optional)"
            type="tel"
            id="demo-phone"
            sx={{ mb: 2 }}
          />

          <TextField
            fullWidth
            label="Preferred Date"
            type="date"
            id="demo-date"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="Preferred Time (Optional)"
            type="time"
            id="demo-time"
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            fullWidth
            label="Additional Message (Optional)"
            id="demo-message"
            multiline
            rows={4}
            placeholder="Let us know if you have any specific topics or features you'd like to discuss..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDemoOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={async () => {
              const nameInput = document.getElementById(
                "demo-name",
              ) as HTMLInputElement;
              const emailInput = document.getElementById(
                "demo-email",
              ) as HTMLInputElement;
              const companyInput = document.getElementById(
                "demo-company",
              ) as HTMLInputElement;
              const phoneInput = document.getElementById(
                "demo-phone",
              ) as HTMLInputElement;
              const dateInput = document.getElementById(
                "demo-date",
              ) as HTMLInputElement;
              const timeInput = document.getElementById(
                "demo-time",
              ) as HTMLInputElement;
              const messageInput = document.getElementById(
                "demo-message",
              ) as HTMLTextAreaElement;

              if (
                !nameInput?.value ||
                !emailInput?.value ||
                !companyInput?.value ||
                !dateInput?.value
              ) {
                setSnackbarMessage("Please fill in all required fields");
                setSnackbarOpen(true);
                return;
              }

              try {
                setSaving(true);
                const response = await api.post("/support/demo", {
                  name: nameInput.value,
                  email: emailInput.value,
                  company: companyInput.value,
                  phone: phoneInput?.value || null,
                  preferred_date: dateInput.value,
                  preferred_time: timeInput?.value || null,
                  message: messageInput?.value || null,
                });

                setSnackbarMessage(
                  `Demo scheduled! Reference: ${response.data.booking_reference}`,
                );
                setSnackbarOpen(true);
                setScheduleDemoOpen(false);

                // Clear form
                if (companyInput) companyInput.value = "";
                if (phoneInput) phoneInput.value = "";
                if (dateInput) dateInput.value = "";
                if (timeInput) timeInput.value = "";
                if (messageInput) messageInput.value = "";
              } catch (error: any) {
                setSnackbarMessage(
                  "Failed to schedule demo. Please try again.",
                );
                setSnackbarOpen(true);
              } finally {
                setSaving(false);
              }
            }}
            disabled={saving}
          >
            {saving ? <CircularProgress size={24} /> : "Schedule Demo"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* SLA Configuration Edit Dialog */}
      <UiDialog
        open={slaConfigDialogOpen}
        onOpenChange={setSlaConfigDialogOpen}
      >
        <UiDialogContent className="sm:max-w-md">
          <UiDialogHeader>
            <UiDialogTitle>Edit SLA configuration</UiDialogTitle>
            <UiDialogDescription>
              Update your Service Level Agreement targets for this workspace.
            </UiDialogDescription>
          </UiDialogHeader>

          <div className="grid gap-4 py-2">
            {editingSlaField === "firstResponseTime" && (
              <div className="space-y-2">
                <Label htmlFor="sla-first-response">
                  First response time (minutes)
                </Label>
                <Input
                  id="sla-first-response"
                  type="number"
                  value={slaConfig.firstResponseTime}
                  onChange={(e) =>
                    setSlaConfig({
                      ...slaConfig,
                      firstResponseTime: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Target time for the first agent response.
                </p>
              </div>
            )}

            {editingSlaField === "resolutionTime" && (
              <div className="space-y-2">
                <Label htmlFor="sla-resolution">Resolution time (hours)</Label>
                <Input
                  id="sla-resolution"
                  type="number"
                  value={slaConfig.resolutionTime}
                  onChange={(e) =>
                    setSlaConfig({
                      ...slaConfig,
                      resolutionTime: e.target.value,
                    })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Target time to resolve conversations.
                </p>
              </div>
            )}

            {editingSlaField === "csatTarget" && (
              <div className="space-y-2">
                <Label htmlFor="sla-csat">
                  Customer satisfaction target (%)
                </Label>
                <Input
                  id="sla-csat"
                  type="number"
                  min={0}
                  max={100}
                  value={slaConfig.csatTarget}
                  onChange={(e) =>
                    setSlaConfig({ ...slaConfig, csatTarget: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Target CSAT score percentage (0–100).
                </p>
              </div>
            )}

            {editingSlaField === "workingHours" && (
              <div className="space-y-2">
                <Label htmlFor="sla-hours">Working hours</Label>
                <Input
                  id="sla-hours"
                  value={slaConfig.workingHours}
                  onChange={(e) =>
                    setSlaConfig({ ...slaConfig, workingHours: e.target.value })
                  }
                />
                <p className="text-xs text-muted-foreground">
                  Example: 9 AM - 6 PM (UTC)
                </p>
              </div>
            )}
          </div>

          <UiDialogFooter className="gap-2 sm:gap-0">
            <UiButton
              type="button"
              variant="outline"
              onClick={() => setSlaConfigDialogOpen(false)}
            >
              Cancel
            </UiButton>
            <UiButton
              type="button"
              onClick={handleSaveSLAConfig}
              disabled={saving}
              className="gap-2"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Save sx={{ fontSize: "1.125rem" }} />
              )}
              {saving ? "Saving…" : "Save"}
            </UiButton>
          </UiDialogFooter>
        </UiDialogContent>
      </UiDialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </TooltipProvider>
  );
};

export default SettingsPage;
